import React, { useState } from "react";
import { graphql, useMutation } from "react-relay";
import { Button } from "src/components/ui/button";
import { Input } from "src/components/ui/input";
import { Label } from "src/components/ui/label";
import { SecretInput } from "src/components/ui/secret-input";
import { ErrorAlert, SuccessAlert } from "src/components/ui/status-alert";
import { Tooltip } from "src/components/ui/tooltip";
import { usePermissions } from "src/context/MeContext";

import { IntegrationType } from "./__generated__/IntegrationsSection_organization.graphql";
import { RollbarIntegrationFormUpdateIntegrationMutation } from "./__generated__/RollbarIntegrationFormUpdateIntegrationMutation.graphql";
import { RollbarIntegrationFormValidateIntegrationMutation } from "./__generated__/RollbarIntegrationFormValidateIntegrationMutation.graphql";

// Define the mutations
const updateIntegrationMutation = graphql`
  mutation RollbarIntegrationFormUpdateIntegrationMutation(
    $input: UpdateIntegrationInput!
  ) {
    updateIntegration(input: $input) {
      integration {
        id
        name
        integrationType
        credentials
      }
      error
    }
  }
`;

const validateIntegrationMutation = graphql`
  mutation RollbarIntegrationFormValidateIntegrationMutation(
    $input: ValidateIntegrationInput!
  ) {
    validateIntegration(input: $input) {
      success
      error
    }
  }
`;

interface RollbarIntegrationFormProps {
  onComplete: (success: boolean) => void;
  onCancel: () => void;
  integrationId?: string;
  initialAccountName?: string;
  initialProjectReadToken?: string;
  editMode?: { integrationId: string; accountName: string; redactedToken: string };
}

export default function RollbarIntegrationForm({
  onComplete,
  onCancel,
  integrationId,
  initialAccountName,
  initialProjectReadToken,
  editMode,
}: RollbarIntegrationFormProps) {
  const [accountName, setAccountName] = useState(editMode?.accountName || initialAccountName || "");
  // In edit mode, leave token blank so the user must paste a fresh one — submitting the
  // bullet-masked redacted value to Rollbar would always 401 and confuse the user.
  const [projectReadToken, setProjectReadToken] = useState(editMode ? "" : (initialProjectReadToken || ""));
  // Optional — auto-detected from the project's existing items, but required for empty projects
  // (no items logged yet) since Rollbar's project-token API doesn't expose project_name elsewhere.
  const [projectName, setProjectName] = useState("");
  // Reveal the Project Name input only when the backend tells us the project is empty
  // (token authenticated but /items returned no records, so we can't auto-detect the slug).
  const [needsProjectName, setNeedsProjectName] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [validationSuccess, setValidationSuccess] = useState<string | null>(null);
  const [isValidated, setIsValidated] = useState(false);
  const { canWrite } = usePermissions();
  const canModify = canWrite("integrations");

  const effectiveIntegrationId = editMode?.integrationId || integrationId;

  // Set up the mutations
  const [commitMutation, isMutationInFlight] = useMutation<RollbarIntegrationFormUpdateIntegrationMutation>(
    updateIntegrationMutation
  );
  const [commitValidateMutation, isValidationInFlight] = useMutation<RollbarIntegrationFormValidateIntegrationMutation>(
    validateIntegrationMutation
  );

  // Handle testing the integration
  const handleTest = () => {
    setFormError(null);
    setValidationSuccess(null);
    setIsValidated(false);

    // Validate required fields
    if (!accountName.trim()) {
      setFormError("Account Name is required");
      return;
    }
    if (!projectReadToken.trim()) {
      setFormError("Project Read Token is required");
      return;
    }

    // Build credentials object — project_name is optional; only used as a fallback when the
    // project has no items yet (so /items can't auto-populate it).
    const credentials = {
      account_name: accountName.trim(),
      project_read_token: projectReadToken.trim(),
      ...(projectName.trim() && { project_name: projectName.trim() }),
    };

    // Prepare the input for the validation mutation
    const input = {
      integrationType: "ROLLBAR" as IntegrationType,
      credentials: JSON.stringify(credentials),
      ...(effectiveIntegrationId && { integrationId: effectiveIntegrationId }),
    };

    // Execute the validation mutation
    commitValidateMutation({
      variables: { input },
      onCompleted: (response, errors) => {
        if (errors && errors.length > 0) {
          const errorMessage = errors.map(e => e.message).join(", ");
          setFormError(errorMessage);
          return;
        }

        if (!response.validateIntegration.success) {
          const errMsg = response.validateIntegration.error || "Validation failed";
          // Empty-project case: backend authenticated the token but /items returned no
          // records, so it can't auto-detect the project slug. Reveal the manual input.
          if (errMsg.includes("enter the project name manually")) {
            setNeedsProjectName(true);
            setFormError(
              "Token authenticated, but this project has no events yet — please enter the Project Name manually below and test again."
            );
          } else {
            setFormError(errMsg);
          }
          return;
        }

        // Success!
        setValidationSuccess("✓ Credentials validated successfully!");
        setIsValidated(true);
      },
      onError: error => {
        setFormError(error.message);
      }
    });
  };

  // Handle saving the integration
  const handleSave = () => {
    setFormError(null);
    setValidationSuccess(null);

    // Validate required fields
    if (!accountName.trim()) {
      setFormError("Account Name is required");
      return;
    }
    if (!projectReadToken.trim()) {
      setFormError("Project Read Token is required");
      return;
    }

    // Build credentials object — project_name is optional; only used as a fallback when the
    // project has no items yet (so /items can't auto-populate it).
    const credentials = {
      account_name: accountName.trim(),
      project_read_token: projectReadToken.trim(),
      ...(projectName.trim() && { project_name: projectName.trim() }),
    };

    // Prepare the input for the mutation
    const input = {
      integrationType: "ROLLBAR" as IntegrationType,
      credentials: JSON.stringify(credentials),
      ...(effectiveIntegrationId && { integrationId: effectiveIntegrationId }),
    };

    // Execute the mutation
    commitMutation({
      variables: { input },
      onCompleted: (response, errors) => {
        if (errors && errors.length > 0) {
          const errorMessage = errors.map(e => e.message).join(", ");
          setFormError(errorMessage);
          onComplete(false);
          return;
        }

        if (response.updateIntegration.error) {
          setFormError(response.updateIntegration.error);
          onComplete(false);
          return;
        }

        // Success!
        onComplete(true);
      },
      onError: error => {
        setFormError(error.message);
        onComplete(false);
      }
    });
  };

  return (
    <div className="space-y-6">
      <div className="space-y-6">
        <div className="border-t border-border pt-4">
          <h3 className="text-base font-semibold text-foreground mb-4">API Token Configuration</h3>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="account-name" className="text-muted-foreground">Account Name</Label>
              <Tooltip
                content={!canModify ? "You don't have permission to modify integrations" : null}
                position="top"
                asChild
              >
                <span className="block w-full">
                  <Input
                    id="account-name"
                    type="text"
                    value={accountName}
                    onChange={(e) => {
                      setAccountName(e.target.value);
                      setIsValidated(false);
                      setValidationSuccess(null);
                    }}
                    placeholder="e.g. acme"
                    disabled={!canModify}
                  />
                </span>
              </Tooltip>
            </div>

            {needsProjectName && (
              <div className="space-y-2">
                <Label htmlFor="project-name" className="text-muted-foreground">Project Name</Label>
                <Tooltip
                  content={!canModify ? "You don't have permission to modify integrations" : null}
                  position="top"
                  asChild
                >
                  <span className="block w-full">
                    <Input
                      id="project-name"
                      type="text"
                      value={projectName}
                      onChange={(e) => {
                        setProjectName(e.target.value);
                        setIsValidated(false);
                        setValidationSuccess(null);
                      }}
                      placeholder="e.g. api-service"
                      disabled={!canModify}
                      autoFocus
                    />
                  </span>
                </Tooltip>
                <p className="text-xs text-muted-foreground">
                  The URL slug after <code>/p/</code> in your Rollbar URL (e.g.{" "}
                  <code>api-service</code>). Required because this project has no events yet —
                  we couldn't auto-detect the slug.
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="project-read-token" className="text-muted-foreground">Project Read Token</Label>
              <Tooltip
                content={!canModify ? "You don't have permission to modify integrations" : null}
                position="top"
                asChild
              >
                <span className="block w-full">
                  <SecretInput
                    id="project-read-token"
                    value={projectReadToken}
                    fieldLabel="Project Read Token"
                    onChange={(e) => {
                      setProjectReadToken(e.target.value);
                      setIsValidated(false);
                      setValidationSuccess(null);
                    }}
                    placeholder="Enter your Rollbar Project Access Token"
                    disabled={!canModify}
                  />
                </span>
              </Tooltip>
            </div>
          </div>
        </div>
      </div>

      {formError && (
        <ErrorAlert icon="none">
          {formError}
        </ErrorAlert>
      )}

      {validationSuccess && (
        <SuccessAlert icon="none">
          {validationSuccess}
        </SuccessAlert>
      )}

      <div className="flex justify-end space-x-3 pt-2">
        <Button variant="outline" onClick={onCancel} disabled={isMutationInFlight || isValidationInFlight}>
          Cancel
        </Button>
        <Tooltip
          content={!canModify ? "You don't have permission to connect integrations" : null}
          position="top"
        >
          <span className="inline-block">
            <Button
              variant="outline"
              onClick={handleTest}
              disabled={isMutationInFlight || isValidationInFlight || !canModify}
            >
              {isValidationInFlight ? "Testing..." : "Test Connection"}
            </Button>
          </span>
        </Tooltip>
        <div className="relative group">
          <Tooltip
            content={!canModify ? "You don't have permission to connect integrations" : null}
            position="top"
          >
            <span className="inline-block">
              <Button
                onClick={handleSave}
                disabled={!isValidated || isMutationInFlight || isValidationInFlight || !canModify}
              >
                {isMutationInFlight ? "Saving..." : "Connect"}
              </Button>
            </span>
          </Tooltip>
          {!isValidated && !isMutationInFlight && !isValidationInFlight && canModify && (
            <div className="absolute bottom-full mb-2 right-0 px-3 py-2 bg-card text-foreground text-sm rounded opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap">
              To connect, please test the connection.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
